import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import privateApi from "../../apis/privateApi";
import Notification from "../../components/Notification/Notification";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import "./BudgetLineCreate.css";

/* ─── react-select styles ────────────────────────────────────── */
const rsStyles = {
    control: (b, s) => ({
        ...b,
        borderColor: s.isFocused ? "#006B3C" : "#D2DDD5",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(0,107,60,.18)" : "none",
        "&:hover": { borderColor: "#006B3C" },
        borderRadius: 6, fontSize: 13, minHeight: 38,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: s.isDisabled ? "#F0F4F1" : "#F0F4F1",
        opacity: s.isDisabled ? 0.6 : 1,
        transition: "border-color .18s, box-shadow .18s",
    }),
    option: (b, s) => ({
        ...b, fontSize: 13,
        background: s.isSelected ? "#006B3C" : s.isFocused ? "rgba(0,107,60,.07)" : "#fff",
        color: s.isSelected ? "#fff" : s.isDisabled ? "#9BB0A0" : "#0E1F14",
    }),
    menu: (b) => ({
        ...b, zIndex: 9999, borderRadius: 10,
        border: "1px solid #D2DDD5",
        boxShadow: "0 12px 40px rgba(1,45,26,.16)",
    }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
    placeholder: (b) => ({ ...b, color: "#9BB0A0", fontSize: 13 }),
    singleValue: (b) => ({ ...b, color: "#0E1F14" }),
};

/* ─── Flatten tree helper ────────────────────────────────────── */
const flatten = (items, depth = 0) => {
    let out = [];
    items.forEach(item => {
        out.push({ ...item, depth });
        if (item.children?.length) out = out.concat(flatten(item.children, depth + 1));
    });
    return out;
};

const buildOptions = (items, labelFn, leafLevel) =>
    flatten(items).map(seg => ({
        value: seg._id,
        label: `${"  ".repeat(seg.depth)}${seg.code ? `${seg.code} — ` : ""}${labelFn(seg)}`,
        isDisabled: leafLevel ? seg.level !== leafLevel : false,
    }));

const BudgetLineCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const budget = location?.state?.budget;

    const [economicCategory, setEconomicCategory] = useState("");
    const [administrativeUnitId, setAdministrativeUnitId] = useState("");
    const [programmeId, setProgrammeId] = useState("");
    const [economicSegmentId, setEconomicSegmentId] = useState("");
    const [fundId, setFundId] = useState("");
    const [functionalSegmentId, setFunctionalSegmentId] = useState("");
    const [geoLocation, setGeoLocation] = useState(null);
    const [amount, setAmount] = useState("");

    const [administrativeUnits, setAdministrativeUnits] = useState([]);
    const [programmes, setProgrammes] = useState([]);
    const [economicSegments, setEconomicSegments] = useState([]);
    const [funds, setFunds] = useState([]);
    const [functions, setFunctions] = useState([]);
    const [geoLocations, setGeoLocations] = useState([]);

    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: "", type: "info" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState("");

    /* Fetch segment data */
    useEffect(() => {
        if (!budget?.level) return;
        (async () => {
            try {
                const [u, p, f, fn, g] = await Promise.all([
                    privateApi.get(`/api/get-create-budget-line-administrative-units?level=${budget.level}`),
                    // privateApi.get(`/api/get-create-budget-line-economic-segments?level=${budget.level}`),
                    privateApi.get(`/api/get-create-budget-line-programmes?level=${budget.level}`),
                    privateApi.get(`/api/get-create-budget-line-funds?level=${budget.level}`),
                    privateApi.get(`/api/get-create-budget-line-functional-segments?level=${budget.level}`),
                    privateApi.get(`/api/budgetline-geo-locations`),
                ]);

                setAdministrativeUnits(u.data.data || []);
                // setEconomicSegments(e.data.data || []);
                setProgrammes(p.data.data || []);
                setFunds(f.data.data || []);
                setFunctions(fn.data.data || []);
                setGeoLocations(g.data.data || []);
            } catch {
                setNotification({ show: true, message: "Failed to load form data.", type: "error" });
            }
        })();
    }, [budget?.level]);

    /* Re-fetch economic segments when category changes */
    useEffect(() => {
        if (!economicCategory) return;
        (async () => {
            try {
                const res = await privateApi.get(`/api/get-create-budget-line-economic-segments`, {
                    params: { category: economicCategory, level: budget.level },
                });
                setEconomicSegments(res.data.data || []);
            } catch {
                setNotification({ show: true, message: "Failed to load economic segments.", type: "error" });
            }
        })();
    }, [economicCategory, budget?.level]);

    /* Build options */
    const adminOptions = buildOptions(administrativeUnits, s => s.description, 5);
    const econOptions = buildOptions(economicSegments, s => s.description, 5);
    const progOptions = buildOptions(programmes, s => s.name, 5);
    const fundOpts = buildOptions(funds, s => s.description, 3);
    const funcOpts = buildOptions(functions, s => s.description, 3);

    const geoOpts =
        budget.level === "lga"
            ? flatten(geoLocations).map(seg => ({
                value: seg._id,
                label: `${"  ".repeat(seg.depth)}${seg.code} — ${seg.name} (${seg.lgaId ? "Ward" : "LGA"})`,
                type: seg.lgaId ? "ward" : "lga",
            }))
            : [
                { value: "LGY", label: "Across LGAs (LGY)", type: "lgy" },
                ...flatten(geoLocations).map(seg => ({
                    value: seg._id,
                    label: `${"  ".repeat(seg.depth)}${seg.code} — ${seg.name} (${seg.lgaId ? "Ward" : "LGA"})`,
                    type: seg.lgaId ? "ward" : "lga",
                })),
            ];

    const formatAmt = v => {
        if (!v) return "";
        return v.toString().replace(/,/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!economicCategory) return setNotification({ show: true, message: "Please select Revenue or Expenditure", type: "error" });
        if (!geoLocation || !["lgy", "lga", "ward"].includes(geoLocation.type))
            return setNotification({ show: true, message: "Invalid geo selection", type: "error" });
        if (!amount || Number(amount) <= 0)
            return setNotification({ show: true, message: "Amount must be greater than zero", type: "error" });
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setConfirmToken(token);
        setConfirmOpen(true);
    };

    const handleConfirm = async input => {
        if (input !== confirmToken) {
            setNotification({ show: true, message: "Incorrect confirmation code.", type: "error" });
            setConfirmOpen(false);
            return;
        }
        setConfirmOpen(false);
        setLoading(true);
        try {
            const payload = {
                budgetId: budget._id,
                administrativeUnitId, programmeId, economicSegmentId,
                fundId, functionalSegmentId,
                amount: Number(amount),
                budgetType: economicCategory,
            };
            if (geoLocation.type === "lgy") { payload.geoScope = "lgy"; payload.stateId = budget.stateId; }
            if (geoLocation.type === "lga") { payload.geoScope = "lga"; payload.lgaId = geoLocation.value; }
            if (geoLocation.type === "ward") { payload.geoScope = "ward"; payload.wardId = geoLocation.value; }

            await privateApi.post("/api/budget-lines", payload);
            setNotification({ show: true, message: "Budget line created successfully.", type: "success" });
            setEconomicCategory(""); setAdministrativeUnitId(""); setProgrammeId("");
            setEconomicSegmentId(""); setFundId(""); setFunctionalSegmentId("");
            setGeoLocation(null); setAmount("");
        } catch (err) {
            setNotification({ show: true, message: err.response?.data?.message || "Failed to create budget line.", type: "error" });
        } finally { setLoading(false); }
    };

    if (!budget) return <p style={{ padding: 40, color: "#6B7F70" }}>No budget selected. Please navigate from the budget list.</p>;

    const locationName = budget?.lgaId?.name || budget?.stateId?.name || "";

    /* Shared Select wrapper to ensure menu z-index */
    const RS = ({ label, required, ...props }) => (
        <div className="bgt-form-group">
            <label className={`bgt-label${required ? " required" : ""}`}>{label}</label>
            <div className="bgt-rs-wrap">
                <Select styles={rsStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    isSearchable isClearable {...props} />
            </div>
        </div>
    );

    return (
        <div className="bgt-page-centered">
            <div style={{ width: "100%", maxWidth: 600 }}>

                <Notification show={notification.show} message={notification.message}
                    type={notification.type} onClose={() => setNotification(p => ({ ...p, show: false }))} />

                <div className="bgt-card" style={{ animation: "bgt-fadeUp .45s ease both" }}>
                    {/* Header */}
                    <div className="bgt-card-header">
                        <h2>Create Budget Line {budget.level === 'state' ? '(State)' : budget.level === 'lga' ? '(LGA)' : ''}</h2>
                        <p>Add a line item to the {locationName} fiscal budget</p>
                    </div>

                    <div className="bgt-form-body">
                        {/* Context chip */}
                        <div className="blc-context-chip">
                            <span className="blc-context-chip-dot" />
                            {locationName} · {budget.year} · {budget.type}
                        </div>

                        {/* Budget type (Revenue / Expenditure) — outside form to control economic segments */}
                        <div className="bgt-form-group" style={{ zIndex: 20 }}>
                            <label className="bgt-label required">Budget Category</label>
                            <div className="bgt-rs-wrap">
                                <Select
                                    styles={rsStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    options={[
                                        { value: "REVENUE", label: "Revenue" },
                                        { value: "EXPENDITURE", label: "Expenditure" },
                                    ]}
                                    value={economicCategory ? { value: economicCategory, label: economicCategory === "REVENUE" ? "Revenue" : "Expenditure" } : null}
                                    onChange={opt => { setEconomicCategory(opt?.value || ""); setEconomicSegmentId(""); }}
                                    placeholder="Select category…"
                                    isClearable
                                />
                            </div>
                        </div>

                        <div className="bgt-form-divider" />
                        <div className="bgt-section-title">Segments</div>

                        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                            <div className="blc-two-col">
                                <RS label="Administrative Unit" required
                                    options={adminOptions}
                                    value={adminOptions.find(o => o.value === administrativeUnitId) || null}
                                    onChange={opt => setAdministrativeUnitId(opt?.value || "")} placeholder="Select unit…" />

                                <RS label="Economic Segment" required
                                    options={econOptions}
                                    value={econOptions.find(o => o.value === economicSegmentId) || null}
                                    onChange={opt => setEconomicSegmentId(opt?.value || "")} placeholder="Select segment…" />

                                <RS label="Functional Segment" required
                                    options={funcOpts}
                                    value={funcOpts.find(o => o.value === functionalSegmentId) || null}
                                    onChange={opt => setFunctionalSegmentId(opt?.value || "")} placeholder="Select function…" />

                                <RS label="Fund" required
                                    options={fundOpts}
                                    value={fundOpts.find(o => o.value === fundId) || null}
                                    onChange={opt => setFundId(opt?.value || "")} placeholder="Select fund…" />

                                <div className="blc-full-col">
                                    <RS label="Programme" required
                                        options={progOptions}
                                        value={progOptions.find(o => o.value === programmeId) || null}
                                        onChange={opt => setProgrammeId(opt?.value || "")} placeholder="Select programme…" />
                                </div>

                                <div className="blc-full-col">
                                    <RS label="Geo Location" required
                                        options={geoOpts}
                                        value={geoLocation}
                                        onChange={opt => setGeoLocation(opt)} placeholder="Select location…" />
                                </div>
                            </div>

                            <div className="bgt-form-divider" />
                            <div className="bgt-section-title">Amount</div>

                            <div className="bgt-form-group">
                                <label className="bgt-label required">Budgeted Amount</label>
                                <div className="bgt-amount-wrap">
                                    <span className="bgt-amount-prefix">₦</span>
                                    <input
                                        className="bgt-input bgt-amount-input"
                                        type="text"
                                        value={formatAmt(amount)}
                                        onChange={e => {
                                            const raw = e.target.value.replace(/,/g, "");
                                            if (!/^\d*$/.test(raw)) return;
                                            setAmount(raw);
                                        }}
                                        placeholder="0"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="bgt-form-actions">
                                <button type="button" className="bgt-btn bgt-btn-secondary" onClick={() => navigate(-1)}>
                                    ← Back
                                </button>
                                <button type="submit" className="bgt-btn bgt-btn-primary" disabled={loading}>
                                    {loading ? "Creating…" : "✓ Create Budget Line"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <ConfirmDialog show={confirmOpen} token={confirmToken}
                action={`You are about to create a new ${budget?.level || "budget"} budget line`}
                onConfirm={handleConfirm}
                onCancel={() => setConfirmOpen(false)} />
        </div>
    );
};

export default BudgetLineCreate;